import React, { useMemo } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  CompanyIdentity,
  companyCellSx,
} from "../../../shared/CompanyIdentity";
import {
  useSorting,
  SortDirection,
} from "../../../SuperInvestors/hooks/useSorting";
import {
  usePagination,
  PaginationResult,
} from "../../../SuperInvestors/hooks/usePagination";
import type { SuperInvestorHoldingsResponse } from "../../../SuperInvestors/hooks/useSuperInvestorHoldings";
import {
  panelTitleSx,
  roundedPanelSx,
  touchTargetSx,
} from "../../overviewPanel";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatNumber } from "../utils/formatNumber";
import {
  getCompanyHoldingRows,
  getPositionRows,
  getPortfolioMoveRows,
  HoldingPositionRow,
  PortfolioMoveRow,
} from "./companyHoldingsData";

const shares = (value: number | null) =>
  value !== null && Number.isFinite(value)
    ? value.toLocaleString("en-US")
    : "—";
const percent = (value: number | null) =>
  value !== null && Number.isFinite(value) ? `${value.toFixed(2)}%` : "—";
const signedShares = (value: number) =>
  `${value > 0 ? "+" : ""}${shares(value)}`;
const signedPercent = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
};
const changeColor = (value: number, theme: Theme) => {
  const shade = theme.palette.mode === "dark" ? "main" : "dark";
  if (value > 0) return theme.palette.success[shade];
  if (value < 0) return theme.palette.error[shade];
  return theme.palette.text.secondary;
};
const tableSx = (theme: Theme) => ({
  tableLayout: "fixed",
  "& .MuiTableCell-root": {
    px: { xs: 1.5, sm: 1, lg: 1.5 },
    py: 1,
    borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
    fontSize: "0.8125rem",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  "& .MuiTableCell-head": {
    bgcolor: "transparent",
    color: "text.secondary",
    fontSize: "0.8125rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    whiteSpace: "normal",
    lineHeight: 1.5,
    py: 1.25,
  },
  "& .holding-figure": {
    fontSize: "0.8125rem",
    fontWeight: 600,
    letterSpacing: "-0.02em",
  },
  "& tbody .holding-primary": { fontSize: "0.8125rem", fontWeight: 700 },
  "& .MuiTableSortLabel-root": {
    color: "inherit",
    "&:hover, &.Mui-active": { color: "primary.main" },
    "& .MuiTableSortLabel-icon": { fontSize: 14 },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
});
const mobileRowSx = (theme: Theme) => ({
  px: 1,
  py: 1.5,
  borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
  fontVariantNumeric: "tabular-nums",
  "& .holding-label": {
    color: "text.secondary",
    fontSize: "0.8125rem",
    textTransform: "none",
    letterSpacing: 0,
    mb: 0.5,
  },
  "& .holding-number": {
    fontSize: "0.8125rem",
    fontWeight: 600,
    letterSpacing: "-0.025em",
  },
});

interface PanelHeadingProps {
  title: string;
  count: number;
  period: string;
}
const PanelHeading: React.FC<PanelHeadingProps> = ({
  title,
  count,
  period,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      flexWrap: "wrap",
      mb: 1.5,
    }}
  >
    <Typography
      component="h2"
      sx={{
        ...panelTitleSx,
        fontSize: "0.875rem",
        color: "text.primary",
        borderLeft: 2,
        borderColor: "primary.main",
        pl: 1,
        lineHeight: 1.5,
      }}
    >
      {title}
    </Typography>
    <Typography
      component="span"
      sx={{
        fontSize: "0.8125rem",
        color: "text.secondary",
        bgcolor: "action.hover",
        px: 0.75,
        py: 0.25,
        borderRadius: 0.75,
      }}
    >
      {count}
    </Typography>
    <Typography
      sx={{ ml: "auto", fontSize: "0.8125rem", color: "text.secondary" }}
    >
      {period}
    </Typography>
  </Box>
);

interface SortHeadingProps {
  label: string;
  field: string;
  direction: SortDirection | null;
  onSort: (key: string) => void;
  primary?: boolean;
}
const SortHeading: React.FC<SortHeadingProps> = ({
  label,
  field,
  direction,
  onSort,
  primary,
}) => (
  <TableCell
    align={field === "company" ? "left" : "right"}
    sortDirection={direction ?? false}
    className={primary ? "holding-primary" : undefined}
    sx={field === "company" ? { width: "34%" } : undefined}
  >
    <TableSortLabel
      active={direction !== null}
      direction={direction ?? "desc"}
      onClick={() => onSort(field)}
      aria-label={`Sort by ${label.toLowerCase()}`}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

interface TableFooterProps {
  pagination: PaginationResult<unknown>;
  title: string;
}
const TableFooter: React.FC<TableFooterProps> = ({ pagination, title }) => {
  if (pagination.totalItems <= 10) return null;
  return (
    <Box
      component="nav"
      aria-label={`${title} pages`}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        flexWrap: "wrap",
        mt: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {pagination.startIndex + 1}–{pagination.endIndex} of{" "}
          {pagination.totalItems}
        </Typography>
        <Select
          size="small"
          value={pagination.pageSize}
          onChange={(event) =>
            pagination.setPageSize(Number(event.target.value))
          }
          inputProps={{ "aria-label": `${title} rows per page` }}
          sx={{
            fontSize: "0.8125rem",
            borderRadius: 1,
            "& .MuiSelect-select": { py: 0.5, pl: 1 },
          }}
        >
          {[10, 25, 50].map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          aria-label={`Previous ${title.toLowerCase()} page`}
          disabled={!pagination.hasPreviousPage}
          onClick={pagination.goToPreviousPage}
          sx={touchTargetSx}
        >
          <ChevronLeft size={16} />
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          {pagination.currentPage} / {pagination.totalPages}
        </Typography>
        <IconButton
          aria-label={`Next ${title.toLowerCase()} page`}
          disabled={!pagination.hasNextPage}
          onClick={pagination.goToNextPage}
          sx={touchTargetSx}
        >
          <ChevronRight size={16} />
        </IconButton>
      </Box>
    </Box>
  );
};

interface PositionTableProps {
  title: string;
  period: string;
  rows: HoldingPositionRow[];
  closed?: boolean;
  compact?: boolean;
}
const PositionTable: React.FC<PositionTableProps> = ({
  title,
  period,
  rows,
  closed = false,
  compact = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currencySymbol, formatDisplayPrice } = useStockCurrency();
  const sorting = useSorting(rows, { key: "value", direction: "desc" });
  const pagination = usePagination(sorting.sortedData, {
    defaultPageSize: compact ? 10 : 25,
  });
  const onSort = (field: string) => {
    sorting.requestSort(field);
    pagination.goToFirstPage();
  };
  const valueLabel = closed ? "Previous value" : "Reported value";
  const weightLabel = closed ? "Previous weight" : "Portfolio weight";
  const value = (amount: number | null) =>
    amount !== null && Number.isFinite(amount)
      ? `${currencySymbol}${formatNumber(amount)}`
      : "—";
  return (
    <Box
      component="section"
      aria-label={title}
      sx={{ ...roundedPanelSx(theme.palette.mode === "dark"), height: "100%" }}
    >
      <PanelHeading title={title} count={rows.length} period={period} />
      {rows.length === 0 && (
        <Box
          role="status"
          sx={{
            minHeight: 140,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No {title.toLowerCase()} this quarter
          </Typography>
        </Box>
      )}
      {rows.length > 0 && !isMobile && (
        <TableContainer sx={{ bgcolor: "transparent" }}>
          <Table
            size="small"
            aria-label={title}
            sx={{
              ...tableSx(theme),
              "& th:nth-of-type(2), & th:last-of-type": { width: "24%" },
            }}
          >
            <TableHead>
              <TableRow>
                <SortHeading
                  label="Company"
                  field="company"
                  direction={sorting.getSortDirection("company")}
                  onSort={onSort}
                />
                <SortHeading
                  label={weightLabel}
                  field="weight"
                  direction={sorting.getSortDirection("weight")}
                  onSort={onSort}
                />
                <SortHeading
                  label="Shares"
                  field="shares"
                  direction={sorting.getSortDirection("shares")}
                  onSort={onSort}
                />
                <SortHeading
                  label={valueLabel}
                  field="value"
                  direction={sorting.getSortDirection("value")}
                  onSort={onSort}
                  primary
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {pagination.currentPageData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={companyCellSx}>
                    <CompanyIdentity
                      company={row.company}
                      symbol={row.symbol}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>
                    {percent(row.weight)}
                  </TableCell>
                  <TableCell align="right" className="holding-figure">
                    {shares(row.shares)}
                  </TableCell>
                  <TableCell
                    align="right"
                    className="holding-figure holding-primary"
                    title={formatDisplayPrice(row.value, 0)}
                  >
                    {value(row.value)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rows.length > 0 &&
        isMobile &&
        pagination.currentPageData.map((row) => (
          <Box key={row.id} sx={mobileRowSx(theme)}>
            <CompanyIdentity company={row.company} symbol={row.symbol} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.5,
                mt: 1.5,
              }}
            >
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography className="holding-label">{weightLabel}</Typography>
                <Typography
                  sx={{ fontSize: "0.8125rem" }}
                  color="text.secondary"
                >
                  {percent(row.weight)}
                </Typography>
              </Box>
              <Box>
                <Typography className="holding-label">Shares</Typography>
                <Typography className="holding-number">
                  {shares(row.shares)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography className="holding-label">{valueLabel}</Typography>
                <Typography
                  title={formatDisplayPrice(row.value, 0)}
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {value(row.value)}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      <TableFooter pagination={pagination} title={title} />
    </Box>
  );
};

interface PortfolioMovesTableProps {
  rows: PortfolioMoveRow[];
  previousPeriod: string;
  currentPeriod: string;
}
const PortfolioMovesTable: React.FC<PortfolioMovesTableProps> = ({
  rows,
  previousPeriod,
  currentPeriod,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sorting = useSorting(rows, { key: "magnitude", direction: "desc" });
  const pagination = usePagination(sorting.sortedData, { defaultPageSize: 25 });
  const onSort = (field: string) => {
    sorting.requestSort(field);
    pagination.goToFirstPage();
  };
  const action = (row: PortfolioMoveRow) => (
    <Typography
      component="span"
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
        flexShrink: 0,
        color: changeColor(row.shareChange, theme),
      }}
    >
      {row.action}
    </Typography>
  );
  return (
    <Box
      component="section"
      aria-label="Biggest portfolio moves"
      sx={roundedPanelSx(theme.palette.mode === "dark")}
    >
      <PanelHeading
        title="Biggest portfolio moves"
        count={rows.length}
        period={`${previousPeriod} → ${currentPeriod}`}
      />
      {rows.length === 0 && (
        <Typography
          role="status"
          variant="body2"
          color="text.secondary"
          sx={{ py: 5, textAlign: "center" }}
        >
          No portfolio moves this quarter
        </Typography>
      )}
      {rows.length > 0 && !isMobile && (
        <TableContainer sx={{ bgcolor: "transparent" }}>
          <Table
            size="small"
            aria-label="Biggest portfolio moves"
            sx={{
              ...tableSx(theme),
              "& th:first-of-type": { width: "30%" },
              [theme.breakpoints.between("sm", "lg")]: {
                minWidth: 640,
                "& th:first-of-type": { width: "26%" },
                "& th:last-of-type": { width: "18%" },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <SortHeading
                  label="Company"
                  field="company"
                  direction={sorting.getSortDirection("company")}
                  onSort={onSort}
                />
                <TableCell>Activity</TableCell>
                <SortHeading
                  label={`${previousPeriod} weight`}
                  field="previousWeight"
                  direction={sorting.getSortDirection("previousWeight")}
                  onSort={onSort}
                />
                <SortHeading
                  label={`${currentPeriod} weight`}
                  field="currentWeight"
                  direction={sorting.getSortDirection("currentWeight")}
                  onSort={onSort}
                />
                <SortHeading
                  label="Share change %"
                  field="shareChangePercent"
                  direction={sorting.getSortDirection("shareChangePercent")}
                  onSort={onSort}
                />
                <SortHeading
                  label="Shares changed"
                  field="shareChange"
                  direction={sorting.getSortDirection("shareChange")}
                  onSort={onSort}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {pagination.currentPageData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={companyCellSx}>
                    <CompanyIdentity
                      company={row.company}
                      symbol={row.symbol}
                    />
                  </TableCell>
                  <TableCell>{action(row)}</TableCell>
                  <TableCell align="right" sx={{ color: "text.secondary" }}>
                    {percent(row.previousWeight)}
                  </TableCell>
                  <TableCell align="right" className="holding-figure">
                    {percent(row.currentWeight)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: changeColor(row.shareChangePercent ?? 0, theme),
                    }}
                  >
                    {signedPercent(row.shareChangePercent)}
                  </TableCell>
                  <TableCell
                    align="right"
                    className="holding-figure"
                    sx={{ color: changeColor(row.shareChange, theme) }}
                  >
                    {signedShares(row.shareChange)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rows.length > 0 &&
        isMobile &&
        pagination.currentPageData.map((row) => (
          <Box key={row.id} sx={mobileRowSx(theme)}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <CompanyIdentity company={row.company} symbol={row.symbol} />
              {action(row)}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.5,
                mt: 1.5,
              }}
            >
              <Box>
                <Typography className="holding-label">
                  {previousPeriod} weight
                </Typography>
                <Typography className="holding-number">
                  {percent(row.previousWeight)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography className="holding-label">
                  {currentPeriod} weight
                </Typography>
                <Typography className="holding-number">
                  {percent(row.currentWeight)}
                </Typography>
              </Box>
              <Box>
                <Typography className="holding-label">
                  Share change %
                </Typography>
                <Typography
                  className="holding-number"
                  sx={{ color: changeColor(row.shareChange, theme) }}
                >
                  {signedPercent(row.shareChangePercent)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography className="holding-label">
                  Shares changed
                </Typography>
                <Typography
                  className="holding-number"
                  sx={{ color: changeColor(row.shareChange, theme) }}
                >
                  {signedShares(row.shareChange)}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      <TableFooter pagination={pagination} title="Portfolio moves" />
    </Box>
  );
};

interface CompanyHoldingsTablesProps {
  data: SuperInvestorHoldingsResponse;
  view?: "all" | "overview" | "holdings";
}
export const CompanyHoldingsTables: React.FC<CompanyHoldingsTablesProps> = ({
  data,
  view = "all",
}) => {
  const currentPeriod = `Q${data.quarter} ${data.year}`;
  const comparison = data.quarterComparison;
  const holdings = useMemo(
    () => getCompanyHoldingRows(data.data, data.totalPortfolioValue),
    [data.data, data.totalPortfolioValue],
  );
  const newPositions = useMemo(
    () => (comparison ? getPositionRows(comparison, "NEW") : []),
    [comparison],
  );
  const closedPositions = useMemo(
    () => (comparison ? getPositionRows(comparison, "CLOSED") : []),
    [comparison],
  );
  const moves = useMemo(
    () => (comparison ? getPortfolioMoveRows(comparison) : []),
    [comparison],
  );
  const previousPeriod = comparison
    ? `Q${comparison.previousQuarter.quarter} ${comparison.previousQuarter.year}`
    : "";
  return (
    <>
      {comparison && view !== "holdings" && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                lg: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <PositionTable
              title="New positions"
              period={currentPeriod}
              rows={newPositions}
              compact
            />
            <PositionTable
              title="Closed positions"
              period={currentPeriod}
              rows={closedPositions}
              compact
              closed
            />
          </Box>
          <PortfolioMovesTable
            rows={moves}
            previousPeriod={previousPeriod}
            currentPeriod={currentPeriod}
          />
        </>
      )}
      {view !== "overview" && (
        <PositionTable
          title="Holding details"
          period={currentPeriod}
          rows={holdings}
        />
      )}
    </>
  );
};
